/**
 * The quote's sections and lines, and — while it can still change — the
 * editor for them (QUOTE-12).
 *
 * Pricing stays cost-driven: the office changes what goes in and what it
 * costs; the sell side is re-derived from total cost at the quote's margin
 * after every edit, so the page always adds up to its total.
 */
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { ApiError, api } from "../lib/api";
import { money, qty } from "../lib/format";
import type { QuoteOut, QuoteSectionOut } from "../lib/types";
import { Card, Chip } from "./ui";

const EDITABLE = new Set(["draft", "awaiting_approval", "sent", "viewed"]);

export function canEditQuote(quote: QuoteOut): boolean {
  return !quote.superseded_by_id && EDITABLE.has(quote.status);
}

function dollars(text: string): number | null {
  const n = Number(text.replace(/[$,\s]/g, ""));
  return Number.isFinite(n) && n >= 0 && text.trim() !== "" ? Math.round(n * 100) : null;
}

export function QuoteSections({
  quote,
  editable,
  onChanged,
}: {
  quote: QuoteOut;
  editable: boolean;
  onChanged: (next: QuoteOut) => void;
}) {
  const [problem, setProblem] = useState<string | null>(null);
  const fail = (err: unknown) => setProblem(err instanceof ApiError ? err.message : "Couldn't save that.");
  const done = (next: QuoteOut) => {
    setProblem(null);
    onChanged(next);
  };

  return (
    <>
      {problem && (
        <p className="notice notice-crit" role="alert">
          {problem}
        </p>
      )}
      {editable && (quote.status === "sent" || quote.status === "viewed") && (
        <p className="notice notice-warn">
          The customer already has the link, and it shows changes straight away. Tell them, or send it again.
        </p>
      )}
      {quote.sections.length === 0 && (
        <Card>
          <p className="tiny muted">Nothing on this quote yet. Add a package, or an empty section to add lines to.</p>
        </Card>
      )}
      {quote.sections.map((section) => (
        <SectionCard key={section.id} quote={quote} section={section} editable={editable} onChanged={done} onError={fail} />
      ))}
      {editable && <AddSection quote={quote} onChanged={done} onError={fail} />}
    </>
  );
}

function SectionCard({
  quote,
  section,
  editable,
  onChanged,
  onError,
}: {
  quote: QuoteOut;
  section: QuoteSectionOut;
  editable: boolean;
  onChanged: (next: QuoteOut) => void;
  onError: (err: unknown) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(section.title);
  const update = useMutation({
    mutationFn: (body: { title?: string; is_optional?: boolean; is_selected?: boolean }) => api.quotes.updateSection(quote.id, section.id, body),
    onSuccess: (next) => {
      setRenaming(false);
      onChanged(next);
    },
    onError,
  });
  const remove = useMutation({ mutationFn: () => api.quotes.removeSection(quote.id, section.id), onSuccess: onChanged, onError });
  const subtotal = section.lines.reduce((sum, line) => sum + line.amount_cents, 0);

  return (
    <Card
      title={
        renaming ? (
          <span className="btn-row">
            <input value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Section title" />
            <button className="btn btn-sm" type="button" onClick={() => update.mutate({ title })}>
              Save
            </button>
          </span>
        ) : (
          section.title
        )
      }
      subtitle={`${section.lines.length} line${section.lines.length === 1 ? "" : "s"} · ${money(subtotal)}`}
      actions={
        <span className="btn-row">
          {section.is_optional && <Chip tone="warn">{section.is_selected ? "Optional — included" : "Optional — excluded"}</Chip>}
          {editable && !renaming && (
            <>
              {section.is_optional && <button className="linkish tiny" type="button" onClick={() => update.mutate({ is_selected: !section.is_selected })}>{section.is_selected ? "Exclude" : "Include"}</button>}
              <button className="linkish tiny" type="button" onClick={() => setRenaming(true)}>
                Rename
              </button>
              <button className="linkish tiny" type="button" onClick={() => update.mutate({ is_optional: !section.is_optional })}>
                {section.is_optional ? "Make standard" : "Make optional"}
              </button>
              <button
                className="linkish tiny"
                type="button"
                onClick={() => {
                  if (window.confirm(`Take "${section.title}" off the quote?`)) remove.mutate();
                }}
              >
                Remove
              </button>
            </>
          )}
        </span>
      }
      padded={false}
    >
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Line</th>
              <th>Qty</th>
              <th className="right phone-hide">Cost</th>
              <th className="right">Charge</th>
              {editable && <th />}
            </tr>
          </thead>
          <tbody>
            {section.lines.map((line) =>
              editable ? (
                <EditableLine key={line.id} quoteId={quote.id} line={line} onChanged={onChanged} onError={onError} />
              ) : (
                <tr key={line.id}>
                  <td>
                    {line.part_number && <div className="tiny muted">{line.part_number}</div>}{line.description}
                    {line.creates_asset && (
                      <>
                        {" "}
                        <Chip tone="accent">registers an asset</Chip>
                      </>
                    )}
                  </td>
                  <td className="mono">
                    {qty(line.qty)} {line.uom}
                  </td>
                  <td className="right mono muted phone-hide">{money(Math.round(line.unit_cost_cents * line.qty))}</td>
                  {/* The stored amount, not unit × qty: multiplying a rounded unit
                      price back out is what made a quote's lines fail to add up
                      to its own total. */}
                  <td className="right mono">{money(line.amount_cents)}</td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
      {editable && <AddLine quoteId={quote.id} sectionId={section.id} onChanged={onChanged} onError={onError} />}
    </Card>
  );
}

function EditableLine({ quoteId, line, onChanged, onError }: {
  quoteId: string; line: QuoteSectionOut["lines"][number];
  onChanged: (next: QuoteOut) => void; onError: (err: unknown) => void;
}) {
  const [form, setForm] = useState({ part_number: line.part_number ?? "", description: line.description, qty: String(line.qty), uom: line.uom,
    cost: (line.unit_cost_cents / 100).toFixed(2),
    mode: line.fixed_price_cents != null ? "price" : line.line_margin_bp != null ? "margin" : "default",
    price: ((line.fixed_price_cents ?? line.unit_sell_cents) / 100).toFixed(2),
    margin: String((line.line_margin_bp ?? 4200) / 100) });
  const update = useMutation({ mutationFn: () => api.quotes.updateLine(quoteId, line.id, {
    part_number: form.part_number.trim() || null, description: form.description.trim(), qty: Number(form.qty), uom: form.uom.trim(), unit_cost_cents: dollars(form.cost)!,
    fixed_price_cents: form.mode === "price" ? dollars(form.price) : null,
    line_margin_bp: form.mode === "margin" ? Math.round(Number(form.margin) * 100) : null,
  }), onSuccess: onChanged, onError });
  const remove = useMutation({ mutationFn: () => api.quotes.removeLine(quoteId, line.id), onSuccess: onChanged, onError });
  const field = (name: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [name]: e.target.value });
  const valid = form.description.trim() && form.uom.trim() && Number(form.qty) > 0 && Number(form.qty) <= 100000
    && dollars(form.cost) !== null && (form.mode !== "price" || dollars(form.price) !== null)
    && (form.mode !== "margin" || (form.margin.trim() && Number(form.margin) >= 0 && Number(form.margin) <= 95));
  return <tr className="quote-line-editor">
    <td><input aria-label="Part number" placeholder="Part number" maxLength={80} value={form.part_number} onChange={field("part_number")} /><input aria-label="Description" value={form.description} onChange={field("description")} style={{ width: "100%" }} /></td>
    <td><input aria-label="Quantity" value={form.qty} onChange={field("qty")} inputMode="decimal" style={{ width: 70 }} />
      <input aria-label="Unit" value={form.uom} onChange={field("uom")} maxLength={20} style={{ width: 70 }} placeholder="hour, m², ea" /></td>
    <td className="right phone-hide"><input aria-label="Unit cost" value={form.cost} onChange={field("cost")} inputMode="decimal" style={{ width: 85 }} /></td>
    <td><select aria-label="Pricing method" value={form.mode} onChange={field("mode")}>
      <option value="default">Quote margin</option><option value="price">Unit selling price</option><option value="margin">Line margin %</option>
    </select>
      {form.mode === "price" && <input aria-label="Unit selling price ex GST before discount" value={form.price} onChange={field("price")} inputMode="decimal" style={{ width: 90 }} />}
      {form.mode === "margin" && <input aria-label="Line margin percent" value={form.margin} onChange={field("margin")} inputMode="decimal" style={{ width: 70 }} />}
      <div className="tiny">Saved total {money(line.amount_cents)}</div>
    </td>
    <td><button className="btn btn-sm" disabled={!valid || update.isPending} onClick={() => update.mutate()}>Save</button>
      <button className="linkish tiny" aria-label={`Remove ${line.description}`} disabled={remove.isPending} onClick={() => remove.mutate()}>Remove</button></td>
  </tr>;
}

function AddLine({
  quoteId,
  sectionId,
  onChanged,
  onError,
}: {
  quoteId: string;
  sectionId: string;
  onChanged: (next: QuoteOut) => void;
  onError: (err: unknown) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [typed, setTyped] = useState({ part_number: "", description: "", qty: "1", cost: "", kind: "part" });
  const items = useQuery({
    queryKey: ["catalogue-items", search.trim()],
    queryFn: () => api.catalogue.items(search.trim() || undefined),
    enabled: open,
    placeholderData: (p) => p,
  });
  const add = useMutation({
    mutationFn: (body: Parameters<typeof api.quotes.addLine>[2]) => api.quotes.addLine(quoteId, sectionId, body),
    onSuccess: (next) => {
      setSearch("");
      setTyped({ part_number: "", description: "", qty: "1", cost: "", kind: "part" });
      onChanged(next);
    },
    onError,
  });

  if (!open) {
    return (
      <div className="btn-row" style={{ padding: "8px 14px" }}>
        <button className="btn btn-sm" type="button" onClick={() => setOpen(true)}>
          Add a line
        </button>
      </div>
    );
  }
  const cost = dollars(typed.cost);
  return (
    <div className="book-form" style={{ display: "block", margin: "8px 14px 12px" }}>
      <label className="field">
        <span>From the catalogue</span>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search parts, labour, hire…" />
      </label>
      {search.trim() && (
        <div className="stack" style={{ marginTop: 6, maxHeight: 220, overflowY: "auto" }}>
          {(items.data ?? []).slice(0, 10).map((item) => (
            <button
              key={item.id}
              type="button"
              className="btn btn-sm"
              style={{ justifyContent: "space-between" }}
              disabled={add.isPending}
              onClick={() => add.mutate({ catalogue_item_id: item.id, qty: 1 })}
            >
              <span>{item.sku ? `${item.sku} - ` : ""}{item.description}</span>
              <span className="mono tiny muted">
                cost {money(item.cost_cents)}/{item.uom}
              </span>
            </button>
          ))}
          {items.data && items.data.length === 0 && <p className="tiny muted">Nothing in the catalogue matches.</p>}
        </div>
      )}
      <p className="tiny muted" style={{ margin: "10px 0 4px" }}>
        Or type one in — it's priced from its cost at the quote's margin.
      </p>
      <div className="form-grid">
        <label className="field">
          <span>Part number (optional)</span>
          <input maxLength={80} value={typed.part_number} onChange={e => setTyped({ ...typed, part_number: e.target.value })} />
        </label>
        <label className="field">
          <span>Description</span>
          <input value={typed.description} onChange={(e) => setTyped({ ...typed, description: e.target.value })} />
        </label>
        <label className="field">
          <span>Qty</span>
          <input value={typed.qty} onChange={(e) => setTyped({ ...typed, qty: e.target.value })} inputMode="decimal" />
        </label>
        <label className="field">
          <span>Unit cost ex GST</span>
          <input value={typed.cost} onChange={(e) => setTyped({ ...typed, cost: e.target.value })} inputMode="decimal" />
        </label>
        <label className="field">
          <span>Kind</span>
          <select value={typed.kind} onChange={(e) => setTyped({ ...typed, kind: e.target.value })}>
            <option value="part">Part</option>
            <option value="labour">Labour</option>
            <option value="hire">Hire</option>
            <option value="fee">Fee</option>
            <option value="subcontract">Subcontract</option>
          </select>
        </label>
      </div>
      <div className="btn-row" style={{ marginTop: 8 }}>
        <button
          className="btn btn-primary btn-sm"
          type="button"
          disabled={add.isPending || !typed.description.trim() || cost === null || !(Number(typed.qty) > 0)}
          onClick={() =>
            add.mutate({
              part_number: typed.part_number.trim() || null,
              description: typed.description.trim(),
              qty: Number(typed.qty),
              unit_cost_cents: cost ?? 0,
              kind: typed.kind,
            })
          }
        >
          Add line
        </button>
        <button className="btn btn-sm" type="button" onClick={() => setOpen(false)}>
          Done
        </button>
      </div>
    </div>
  );
}

function AddSection({
  quote,
  onChanged,
  onError,
}: {
  quote: QuoteOut;
  onChanged: (next: QuoteOut) => void;
  onError: (err: unknown) => void;
}) {
  const [open, setOpen] = useState(false);
  const [kitId, setKitId] = useState("");
  const [driver, setDriver] = useState("1");
  const [extras, setExtras] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const kits = useQuery({ queryKey: ["kits"], queryFn: () => api.catalogue.kits(), enabled: open });
  const kit = kits.data?.find((k) => k.id === kitId);
  const add = useMutation({
    mutationFn: () =>
      api.quotes.addSection(
        quote.id,
        kitId ? { kit_id: kitId, include_optional_item_ids: extras, driver_qty: Number(driver) || 1, title: title.trim() || undefined } : { title: title.trim() || "Section" },
      ),
    onSuccess: (next) => {
      setOpen(false);
      setKitId("");
      setTitle("");
      onChanged(next);
    },
    onError,
  });

  if (!open) {
    return (
      <div className="btn-row" style={{ marginBottom: 16 }}>
        <button className="btn" type="button" onClick={() => setOpen(true)}>
          Add a section
        </button>
      </div>
    );
  }
  return (
    <Card title="Add a section" subtitle="A package expands into its lines; an empty one is for lines you add yourself">
      <div className="form-grid">
        <label className="field">
          <span>Package</span>
          <select
            value={kitId}
            onChange={(e) => {
              setKitId(e.target.value);
              setExtras([]);
              const k = kits.data?.find((x) => x.id === e.target.value);
              if (k) setDriver(String(k.default_driver_qty));
            }}
          >
            <option value="">Empty section</option>
            {(kits.data ?? []).map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </select>
        </label>
        {kit && (
          <label className="field">
            <span>How many {kit.driver_label}</span>
            <input value={driver} onChange={(e) => setDriver(e.target.value)} inputMode="decimal" />
          </label>
        )}
        <label className="field">
          <span>Title {kit ? "(optional)" : ""}</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={kit?.name ?? "e.g. Extras"} />
        </label>
      </div>
      {kit?.optional_items?.map(item => <label key={item.id} style={{ display: "block", margin: "8px 0" }}><input type="checkbox" checked={extras.includes(item.id)} onChange={e => setExtras(e.target.checked ? [...extras, item.id] : extras.filter(id => id !== item.id))} /> Include package extra: {item.description}</label>)}
      <div className="btn-row" style={{ marginTop: 10 }}>
        <button className="btn btn-primary" type="button" disabled={add.isPending || (Boolean(kitId) && !(Number(driver) > 0))} onClick={() => add.mutate()}>
          {add.isPending ? "Adding…" : "Add section"}
        </button>
        <button className="btn" type="button" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </Card>
  );
}

/** Title, deposit and expiry. */
export function QuoteHeaderCard({ quote, onChanged }: { quote: QuoteOut; onChanged: (next: QuoteOut) => void }) {
  const [title, setTitle] = useState(quote.title);
  const [deposit, setDeposit] = useState(String(quote.deposit_pct_bp / 100));
  const [validUntil, setValidUntil] = useState(quote.valid_until ?? "");
  const [scope, setScope] = useState(quote.scope ?? "");
  const [exclusions, setExclusions] = useState(quote.exclusions ?? "");
  const [terms, setTerms] = useState(quote.terms ?? "");
  const [discount, setDiscount] = useState(String((quote.discount_bp ?? 0) / 100));
  const [problem, setProblem] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api.quotes.update(quote.id, {
        title: title.trim(),
        deposit_pct_bp: Math.round((Number(deposit) || 0) * 100),
        valid_until: validUntil || null,
        scope, exclusions, terms, discount_bp: Math.round(Number(discount) * 100),
      }),
    onSuccess: (next) => {
      setProblem(null);
      onChanged(next);
    },
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't save that."),
  });
  const dirty = scope !== (quote.scope ?? "") || exclusions !== (quote.exclusions ?? "") || terms !== (quote.terms ?? "") || Math.round(Number(discount) * 100) !== (quote.discount_bp ?? 0) ||
    title.trim() !== quote.title ||
    Math.round((Number(deposit) || 0) * 100) !== quote.deposit_pct_bp ||
    (validUntil || null) !== (quote.valid_until ?? null);
  return (
    <Card title="Quote">
      <div className="stack">
        <label className="field">
          <span>Title</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="field">
          <span>Deposit on acceptance (%)</span>
          <input value={deposit} onChange={(e) => setDeposit(e.target.value)} inputMode="decimal" />
        </label>
        <label className="field">
          <span>Valid until</span>
          <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
        </label>
        <label className="field"><span>Scope and inclusions</span><textarea rows={4} value={scope} onChange={e => setScope(e.target.value)} /></label>
        <label className="field"><span>Exclusions</span><textarea rows={3} value={exclusions} onChange={e => setExclusions(e.target.value)} /></label>
        <label className="field"><span>Payment terms and conditions</span><textarea rows={4} value={terms} onChange={e => setTerms(e.target.value)} /></label>
        <label className="field"><span>Discount (%)</span><input type="number" min="0" max="100" step="0.01" value={discount} onChange={e => setDiscount(e.target.value)} /></label>
        {problem && <p className="notice notice-crit">{problem}</p>}
        <div className="btn-row">
          <button className="btn btn-sm" type="button" disabled={!dirty || !title.trim() || save.isPending || !Number.isFinite(Number(discount)) || Number(discount) < 0 || Number(discount) > 100} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </Card>
  );
}
